import { useState, useEffect } from "react";

const FileUploader = ({
  name,
  required,
  register,
  errors,
  label = "Upload File",
  accept = ".pdf,.doc,.docx,image/*",
  maxSize = 5 * 1024 * 1024,
  onFileSelect,
  labelClass = "font-bold text-base",
  errorMessages = { type: "Invalid file type", size: "File is too large" },
  defaultValue,
}) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(defaultValue || "");
  const [error, setError] = useState("");

  const isRemoteUrl = typeof previewUrl === "string" && /^https?:\/\//.test(previewUrl);
  const viewLabel = /photo/i.test(label) ? "View photo" : /cv/i.test(label) ? "View CV" : "View file";

  useEffect(() => {
    if (defaultValue && typeof defaultValue === "string") {
      setPreviewUrl(defaultValue);
    }
  }, [defaultValue]);

  const extractFileName = (val) => {
    if (!val) return "";
    if (file?.name) return file.name;
    try {
      const url = new URL(val);
      return decodeURIComponent(url.pathname.split("/").pop() || val);
    } catch {
      return decodeURIComponent(val.split("/").pop() || val);
    }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const validExt = accept.split(",").map((ext) => ext.trim());
    const ext = `.${selected.name.split(".").pop().toLowerCase()}`;

    if (!validExt.includes(ext) && !selected.type.startsWith("image/")) {
      setError(errorMessages.type);
      resetFile();
      return;
    }

    if (selected.size > maxSize) {
      setError(errorMessages.size);
      resetFile();
      return;
    }

    const preview = selected.type.startsWith("image/") ? URL.createObjectURL(selected) : selected.name;

    setFile(selected);
    setPreviewUrl(preview);
    setError("");
    onFileSelect?.(selected);
  };

  const resetFile = () => {
    setFile(null);
    setPreviewUrl("");
    setError("");
    onFileSelect?.(null);
  };

  return (
    <div className="flex flex-col w-full">
      <label htmlFor={label} className={labelClass}>
        {label}
        {required && <span className="text-red-500 text-xs ml-0.5">*</span>}
      </label>

      <div className="mt-2">
        <div
          className={`border-2 border-dashed rounded-lg p-4 sm:p-6 text-center transition-colors ${
            file || previewUrl
              ? "border-purple-400 bg-purple-50"
              : error
              ? "border-red-400 bg-red-50"
              : "border-gray-300 hover:border-gray-400"
          }`}
        >
          <div className="flex flex-col items-center">
            <svg
              className="w-12 h-12 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={file || previewUrl ? 2 : 1.5}
                d={
                  file || previewUrl
                    ? "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    : "M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                }
              />
            </svg>

            <input
              type="file"
              accept={accept}
              id={`file-${name}`}
              onChange={handleFileChange}
              className="hidden"
            />

            <label
              htmlFor={`file-${name}`}
              className={`rounded-md px-4 py-2 text-sm font-medium cursor-pointer transition-colors ${
                file || previewUrl
                  ? "bg-purple-100 border border-purple-300 text-purple-700 hover:bg-purple-200"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              {file || previewUrl ? "Change File" : "Choose File"}
            </label>

            {file || previewUrl ? (
              <div className="mt-3 text-center">
                {isRemoteUrl ? (
                  <div className="flex justify-center gap-4">
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-purple-600 underline"
                    >
                      {viewLabel}
                    </a>
                    <button
                      type="button"
                      onClick={resetFile}
                      className="text-sm text-red-600 hover:text-red-700 underline"
                    >
                      Remove File
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-medium text-purple-700">
                      ✓ {extractFileName(previewUrl)}
                    </p>
                    <button
                      type="button"
                      onClick={resetFile}
                      className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
                    >
                      Remove File
                    </button>
                  </>
                )}
              </div>
            ) : (
              <p className="mt-2 text-xs text-gray-500">
                Supported formats:{" "}
                {accept.includes("image") ? "JPEG, PNG, GIF" : "PDF, DOC, DOCX"}
              </p>
            )}
          </div>
        </div>
      </div>

      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      {errors?.[name]?.message && (
        <p className="mt-1 text-sm text-red-500">{errors[name].message}</p>
      )}
    </div>
  );
};

export default FileUploader;
